import { useMemo } from "react";
import {
  SECTION_ORDER,
  type BenefitSectionDef,
  normalizePlanType,
  getSectionForType,
} from "@shared/schemas/planTypes";

export interface TypeSection {
  def: BenefitSectionDef;
  /** Active (and currently-effective) base enrollments in this section. */
  activeRows: any[];
  /** Waived base enrollments in this section. */
  waivedRows: any[];
  /** Count of pending enrollments tracked at the section level (still rendered in the global Upcoming Benefits block). */
  pendingCount: number;
  /**
   * Plan-type ids the client offers in this section that have no enrollment
   * of any kind on file for the employee. Each gets an "empty" row CTA.
   */
  emptyTypes: string[];
  /** True when the client offers at least one plan whose type belongs in this section. */
  hasAnyClientPlans: boolean;
}

export interface UseTypeSectionsResult {
  sections: TypeSection[];
  /** All type ids the client offers that the employee hasn't decided on yet. */
  unconfiguredTypes: string[];
  /** unconfiguredTypes.length — used to gate the global Configure Benefits button. */
  remainingCount: number;
  segmentCounts: {
    enrolled: number;
    pending: number;
    waived: number;
    notYet: number;
  };
}

function normalize(t: any): string {
  return normalizePlanType(t);
}

export interface ComputeTypeSectionsInput {
  activeBenefits: any[];
  pendingBenefits: any[];
  waivedBenefits: any[];
  clientPlans: any[]; // [{ plan: { id, type, ... }, ... }]
}

/**
 * Pure section-derivation logic. Kept separate from the hook so it can be
 * unit-tested without React. The hook below is a thin `useMemo` wrapper.
 */
export function computeTypeSections({
  activeBenefits,
  pendingBenefits,
  waivedBenefits,
  clientPlans,
}: ComputeTypeSectionsInput): UseTypeSectionsResult {
  // Build set of types the client offers (normalized).
  const offeredTypes = new Set<string>();
  for (const cp of clientPlans || []) {
    const t = normalize(cp?.plan?.type);
    if (t) offeredTypes.add(t);
  }

  // Bucket enrollments by section id.
  type Bucket = { active: any[]; pending: any[]; waived: any[]; typesWithRows: Set<string> };
  const bySection = new Map<string, Bucket>();
  const ensureBucket = (id: string): Bucket => {
    let b = bySection.get(id);
    if (!b) {
      b = { active: [], pending: [], waived: [], typesWithRows: new Set<string>() };
      bySection.set(id, b);
    }
    return b;
  };

  const place = (enrollment: any, kind: "active" | "pending" | "waived") => {
    const t = normalize(enrollment?.plan?.type);
    const section = getSectionForType(t);
    const b = ensureBucket(section.id);
    b[kind].push(enrollment);
    b.typesWithRows.add(t);
  };

  for (const e of activeBenefits || []) place(e, "active");
  for (const e of pendingBenefits || []) place(e, "pending");
  for (const e of waivedBenefits || []) place(e, "waived");

  // Build the ordered section list.
  const sections: TypeSection[] = SECTION_ORDER.map((def) => {
    const bucket = bySection.get(def.id) ?? {
      active: [],
      pending: [],
      waived: [],
      typesWithRows: new Set<string>(),
    };
    // Which canonical type ids in this section does the client offer
    // AND the employee has no enrollment for?
    const sectionOfferedTypes = def.types.filter((t) => offeredTypes.has(t));
    // Deduplicate medical/health collisions: SECTION_ORDER lists both
    // so we treat "medical" and "health" as the same slot for the empty row.
    const sectionCanonicalTypes = Array.from(
      new Set(sectionOfferedTypes.map((t) => (t === "health" ? "medical" : t))),
    );
    const emptyTypes = sectionCanonicalTypes.filter((t) => {
      // For medical, also consider "health" alias for "rows" check.
      if (t === "medical") {
        return !bucket.typesWithRows.has("medical") && !bucket.typesWithRows.has("health");
      }
      return !bucket.typesWithRows.has(t);
    });
    const hasAnyClientPlans = sectionOfferedTypes.length > 0;
    return {
      def,
      activeRows: bucket.active,
      waivedRows: bucket.waived,
      pendingCount: bucket.pending.length,
      emptyTypes,
      hasAnyClientPlans,
    };
  });

  // Unconfigured = offered types with no enrollment of any kind.
  const enrolledTypes = new Set<string>();
  for (const e of [...(activeBenefits || []), ...(pendingBenefits || []), ...(waivedBenefits || [])]) {
    const t = normalize(e?.plan?.type);
    if (t) {
      enrolledTypes.add(t);
      if (t === "health") enrolledTypes.add("medical");
      if (t === "medical") enrolledTypes.add("health");
    }
  }
  const unconfiguredTypes = Array.from(offeredTypes)
    .filter((t) => !enrolledTypes.has(t))
    // Collapse health → medical for the count so the same physical "slot"
    // isn't counted twice when the client offers both.
    .map((t) => (t === "health" ? "medical" : t))
    .filter((v, i, arr) => arr.indexOf(v) === i);

  return {
    sections,
    unconfiguredTypes,
    remainingCount: unconfiguredTypes.length,
    segmentCounts: {
      enrolled: (activeBenefits || []).length,
      pending: (pendingBenefits || []).length,
      waived: (waivedBenefits || []).length,
      notYet: unconfiguredTypes.length,
    },
  };
}

export function useTypeSections(input: ComputeTypeSectionsInput): UseTypeSectionsResult {
  const { activeBenefits, pendingBenefits, waivedBenefits, clientPlans } = input;
  return useMemo(
    () => computeTypeSections({ activeBenefits, pendingBenefits, waivedBenefits, clientPlans }),
    [activeBenefits, pendingBenefits, waivedBenefits, clientPlans],
  );
}
